// ------------------------------------------------------------------------------------------------------
import * as React from 'react';
import _ from 'lodash';
import { RGBColor } from 'react-color';
import { Highlight } from 'canvas-gauges';
import { arrayMoveImmutable } from 'array-move';

// Material UI
import Dialog from 'material-ui/Dialog';
import { Toolbar, ToolbarGroup, ToolbarTitle } from 'material-ui/Toolbar';
import { TableHeader, TableHeaderColumn, TableRow, TableRowColumn } from 'material-ui/Table';
import FlatButton from 'material-ui/FlatButton';
import IconButton from 'material-ui/IconButton';
import TextField from 'material-ui/TextField';
import Paper from 'material-ui/Paper';
import { SketchPicker } from 'react-color';
import Popover from 'material-ui/Popover/Popover';
import { List, ListItem } from 'material-ui/List';
import IconMenu from 'material-ui/IconMenu';
import MenuItem from 'material-ui/MenuItem';
import Chip from 'material-ui/Chip';

import MoreVertIcon from 'material-ui/svg-icons/navigation/more-vert';
import AddIcon from 'material-ui/svg-icons/content/add';
import DeleteIcon from 'material-ui/svg-icons/action/delete';
import EditIcon from 'material-ui/svg-icons/editor/mode-edit';
import ReorderIcon from 'material-ui/svg-icons/action/reorder';
import OpenIcon from 'material-ui/svg-icons/action/touch-app';
import KeyboardRightArrowIcon from 'material-ui/svg-icons/hardware/keyboard-arrow-right';
import KeyboardLeftArrowIcon from 'material-ui/svg-icons/hardware/keyboard-arrow-left';
import SearchIcon from 'material-ui/svg-icons/action/search';

import { grey300, grey500 } from 'material-ui/styles/colors';

// Internal
import { DebugEx } from "@Utils/DebugEx";
import { BoardTheming } from '@UI/Dashboard/BoardTheming';
import { getZDepthShadow } from '@Utils/Colors';
import { isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';
import { isAnimationEnabled } from '@Utils/Globals';
import { SearchBar } from '@Primitive/SearchBar';
import { DialogConf, ExDialog } from '@Primitive/ExDialog';
import "../../../Primitive/PrimitiveCss.less";

const Sortable = React.lazy(() => import("@Primitive/Sortable"));
import { DraggableIconStyle, SortableTableRowStyle } from '@Primitive/SortableConst';
import { LazyLoadingAnimation } from '@Primitive/Lazy';
// ------------------------------------------------------------------------------------------------------

export function getValueFromHighlights(value: number, highlights: GaugeHelperHighlight[]): {
    success: boolean,
    color: string,
    textColor: string
} {
    const result = { success: false, color: '', textColor: '' };

    // if there is any highlight
    if (isDefined(highlights) && highlights.length > 0) {

        // get highlight for knob's value.
        let highlight: GaugeHelperHighlight = highlights.find(highlight => {
            return highlight.from <= value && highlight.to >= value;
        });

        // update result entry found
        if (isDefined(highlight)) {
            result.color = highlight.color;
            result.textColor = highlight.textColor;
            result.success = true;
            return result;
        }
    }

    return result;
}
// ------------------------------------------------------------------------------------------------------
// todo: check if we can remove this function
export function getStringFromRGBA(color: RGBColor): string {
    return `rgba(${color.r},${color.g},${color.b},${color.a})`;
}
// ------------------------------------------------------------------------------------------------------
// todo: remove
export function getRGBAFromString(color: string): RGBColor {
    var splitted = color.replace("rgba(", "").replace(")", "").split(",");
    return {
        r: Number(splitted[0]),
        g: Number(splitted[1]),
        b: Number(splitted[2]),
        a: Number(splitted[3]),
    };
}
// ------------------------------------------------------------------------------------------------------
export function validateHighlights(minValue: number, maxValue: number, highlights: GaugeHelperHighlight[]): boolean {
    return validateHighlightsWithMsg(minValue, maxValue, highlights).valid;
}
// --------------------------------------------------------------------------------------------
export function validateHighlightsWithMsg(minValue: number, maxValue: number, highlights: GaugeHelperHighlight[]): { valid: boolean, errorMsg?: string | React.ReactNode } {
    // protect against invalid data type
    if (!_.isArray(highlights))
        return {
            valid: false,
            errorMsg: ''
        };

    if (isDefined(minValue) && isDefined(maxValue))
        for (let i = 0; i < highlights.length; i++) {
            const el = highlights[i];

            // check values
            if (!isDefined(el.from) || !isDefined(el.to) || _.isNaN(el.from) || _.isNaN(el.to))
                return {
                    valid: false,
                    errorMsg: "From and To values should be numeric"
                }

            // check ranges
            if (minValue > el.from || minValue > el.to ||
                maxValue < el.from || maxValue < el.to)
                return {
                    valid: false,
                    errorMsg: 'Please check again your From and To fields which should be included betweeen Min and Max values.'
                }

            // check `from` `to`
            if (el.from > el.to)
                return {
                    valid: false,
                    errorMsg: "From values should be lower than To values"
                }
        }
    else
        for (let i = 0; i < highlights.length; i++) {
            const el = highlights[i];

            // check values
            if (!isDefined(el.from) || !isDefined(el.to) || _.isNaN(el.from) || _.isNaN(el.to))
                return {
                    valid: false,
                    errorMsg: "From and To values should be numeric"
                }

            // check `from` `to`
            if (el.from > el.to)
                return {
                    valid: false,
                    errorMsg: "From values should be lower than To values"
                }
        }

    // no error; return true
    return {
        valid: true,
        errorMsg: ''
    };
}

// ---------------------------------------------------------------------------------------------------
export interface HighlightsEditorProps {

    /**
     * Whether dialog is visible or not
     */
    isVisible: boolean;

    /**
     * Whether to display float values or not
     */
    enableDecimal?: boolean

    /**
     * Normalized percentage of window's width to be used. Ranges in [0,1], default: 0.8.
     */
    widthPercentage?: number;

    /**
     * Normalized percentage of window's height to be used. Ranges in [0,1], default: 0.8.
     */
    heightPercentage?: number;

    /**
     * Highlights
     */
    highlights: GaugeHelperHighlight[];

    /**
     * Whether to diplay color picker for text color picking, or not.
     */
    showTextColor: boolean;

    /**
     * Minimum integer value
     */
    minValue: number;

    /**
     *
     * Maximum integer value
     */
    maxValue: number;

    /**
     * Callback when user requests save
     */
    onSave: (newHighlights: GaugeHelperHighlight[]) => void;

    /**
     * Callback when user aborts
     */
    onCancel: () => void;
}
// ------------------------------------------------------
export interface HighlightsEditorState {
    width: number;
    height: number;
    highlights: GaugeHelperHighlight[];
    showErrorDialog: boolean;
    errorMessage: string;
}
// ------------------------------------------------------
export interface GaugeHelperHighlight extends Highlight {
    textColor?: string;
}
// ------------------------------------------------------
export interface IHighlightBounds {
    min: number;
    max: number;
}
// ------------------------------------------------------
export declare type GaugeHelperHighlightDictionary = { [key: string]: GaugeHelperHighlight[] };
// ------------------------------------------------------
// create HighlightJsonScheme class for json validation
export function HighlightJsonSchemeValidator() {
    return JsonSchemaValidator.GetInstance("HighlightJsonSchemeValidator", {
        title: "SelectDataType",
        type: "array",
        items: {
            type: "object",
            properties: {
                from: {
                    type: "string"
                },
                to: {
                    type: "string"
                },
                color: { type: "string" },
                textColor: { type: "string" },
            },
            required: ["from", "to", "color", "textColor"]
        },
        minItems: 1,
    });
}
// ------------------------------------------------------------------------------------------------------
export interface SortableHighlightsTableProps {
    height: number;
    width: number;

    highlights: GaugeHelperHighlight[];

    enableDecimal?: boolean;

    /**
     * Whether to display color picker for text color, or not.
     */
    showTextColor: boolean;

    /**
     * Minimum integer value
     */
    minValue: number;

    /**
     *
     * Maximum integer value
     */
    maxValue: number;

    /**
     * Callback called when user re-orders rows or deletes rows
     */
    onChange: (newHighlights: GaugeHelperHighlight[]) => void;
}
export interface SortableHighlightsTableState {
    showColorPicker: boolean[];
    showTextColorPicker: boolean[];
}
// ------------------------------------------------------------------------------------------------------
const tableCellCommonStyle: React.CSSProperties = { textAlign: 'center' };
// ------------------------------------------------------------------------------------------------------
export class SortableHighlightsTable extends React.Component<SortableHighlightsTableProps, SortableHighlightsTableState>{
    // ------------------------------------------------------------------------------------------------------
    // Set default values
    static defaultProps: SortableHighlightsTableProps = {
        height: 120,
        width: 120,
        highlights: [],
        maxValue: Number.MAX_SAFE_INTEGER,
        minValue: Number.MIN_SAFE_INTEGER,
        showTextColor: false,
        onChange: _.noop,
        enableDecimal: false
    };
    // ------------------------------------------------------------------------------------------------------
    private colorPickerDivs: HTMLDivElement[];
    private textColorPickerDivs: HTMLDivElement[];
    // ------------------------------------------------------------------------------------------------------
    constructor(props) {
        super(props);
        this.state = {
            showColorPicker: this.props.highlights.map(__ => false),
            showTextColorPicker: this.props.highlights.map(__ => false)
        };
        this.colorPickerDivs = this.props.highlights.map(__ => null);
        this.textColorPickerDivs = this.props.highlights.map(__ => null);
    }
    // ------------------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: SortableHighlightsTableProps) {
        // close all color pickers; if highlights' length did change
        if (this.props.highlights.length !== prevProps.highlights.length) {
            this.colorPickerDivs = this.props.highlights.map(__ => null);
            this.textColorPickerDivs = this.props.highlights.map(__ => null);

            this.setState({
                showColorPicker: this.props.highlights.map(__ => false),
                showTextColorPicker: this.props.highlights.map(__ => false)
            });
        }
    }
    // ------------------------------------------------------------------------------------------------------
    private _onChange(newHighlights: GaugeHelperHighlight[]) {
        if (isDefined(this.props.onChange))
            try {
                this.props.onChange(newHighlights)
            }
            catch (ex) {
                DebugEx.TraceError(ex);
            }
    }
    // ------------------------------------------------------------------------------------------------------
    private _renderColorPicker(highlight: GaugeHelperHighlight, index: number, forTextColor: boolean): React.ReactElement<any> {
        return (
            <div>
                <div
                    style={{
                        padding: '5px',
                        background: '#fff',
                        borderRadius: '1px',
                        boxShadow: '0 0 0 1px rgba(0,0,0,.1)',
                        display: 'inline-block',
                        cursor: 'pointer'
                    }}
                    ref={(r: HTMLDivElement) => {
                        if (forTextColor)
                            this.textColorPickerDivs[index] = r;
                        else
                            this.colorPickerDivs[index] = r

                    }}
                    onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                        this.setState((s) => {
                            if (forTextColor)
                                s.showTextColorPicker[index] = !s.showTextColorPicker[index];
                            else
                                s.showColorPicker[index] = !s.showColorPicker[index];
                            return s;
                        });
                    }}
                >
                    <div
                        style={{
                            width: '36px',
                            height: '14px',
                            borderRadius: '2px',
                            background: forTextColor ? highlight.textColor : highlight.color
                        }}
                    />
                </div>
                <Popover
                    open={forTextColor ? this.state.showTextColorPicker[index] : this.state.showColorPicker[index]}
                    anchorEl={forTextColor ? this.textColorPickerDivs[index] : this.colorPickerDivs[index]}
                    anchorOrigin={{
                        'horizontal': 'left',
                        'vertical': 'bottom'
                    }}
                    targetOrigin={{
                        'horizontal': 'left',
                        'vertical': 'top'
                    }}
                    onRequestClose={() =>
                        this.setState((s) => {
                            if (forTextColor)
                                s.showTextColorPicker[index] = false;
                            else
                                s.showColorPicker[index] = false;
                            return s;
                        })
                    }
                >
                    <SketchPicker
                        color={forTextColor ? highlight.textColor : highlight.color}
                        onChange={(color) => {
                            if (forTextColor)
                                this.props.highlights[index].textColor = getStringFromRGBA(color.rgb);
                            else
                                this.props.highlights[index].color = getStringFromRGBA(color.rgb);
                            this._onChange(this.props.highlights);
                        }}
                    />
                </Popover>
            </div>
        );
    }
    // ------------------------------------------------------------------------------------------------------
    render() {
        //TODO make it more flexible through setting list
        return (
            <div
                style={{
                    maxHeight: this.props.height,
                    width: this.props.width,
                    overflow: 'hidden'
                }}
            >
                <React.Suspense fallback={<LazyLoadingAnimation />}>
                    <Sortable
                        tableId={'table'}
                        tableHeader={
                            <TableHeader
                                displaySelectAll={false}
                                adjustForCheckbox={false}
                                enableSelectAll={false}
                            >
                                <TableRow key='thead-tr'>
                                    <TableHeaderColumn colSpan={1} key="table-row-reorder-icon" style={tableCellCommonStyle} />
                                    <TableHeaderColumn
                                        key='thead-tr-1st-col'
                                        style={tableCellCommonStyle}
                                    >
                                        From
                                    </TableHeaderColumn>
                                    <TableHeaderColumn
                                        key='thead-tr-2nd-col'
                                        style={tableCellCommonStyle}
                                    >
                                        To
                                    </TableHeaderColumn>
                                    <TableHeaderColumn
                                        key='thead-tr-3rd-col'
                                        style={tableCellCommonStyle}
                                    >
                                        Color
                                    </TableHeaderColumn>
                                    {this.props.showTextColor ?
                                        <TableHeaderColumn
                                            key='thead-tr-4rd-col'
                                            style={tableCellCommonStyle}>
                                            Text Color
                                        </TableHeaderColumn>
                                        : null}
                                    <TableHeaderColumn
                                        key='thead-tr-5th-col'
                                        style={tableCellCommonStyle}
                                    />
                                </TableRow>
                            </TableHeader>
                        }
                        items={this.props.highlights.map((highlight, index) => {
                            return (
                                <TableRow

                                    key={'highlight-tr-' + index}
                                    displayBorder={true}
                                    striped={index % 2 == 0 ? true : false}
                                    style={SortableTableRowStyle}
                                >
                                    <TableRowColumn
                                        colSpan={1}
                                        style={tableCellCommonStyle}>
                                        <ReorderIcon
                                            className={'draggable-item'}
                                            style={DraggableIconStyle}
                                        />
                                    </TableRowColumn>
                                    <TableRowColumn
                                        key='highlight-tr-1st-col'
                                        style={tableCellCommonStyle}
                                    >
                                        <TextField
                                            key={'highlight-from' + index}
                                            id={'highlight-from' + index}
                                            type='number'
                                            value={highlight.from}
                                            min={this.props.minValue}
                                            max={this.props.maxValue}
                                            fullWidth={true}
                                            errorText={(highlight.from < this.props.minValue ||
                                                highlight.from > this.props.maxValue ||
                                                highlight.from > highlight.to ||
                                                _.isNaN(highlight.from)) ? ' ' : ''}
                                            onChange={(event: object, newValue: string) => {
                                                this.props.highlights[index].from = this._parseValue(newValue);
                                                this._onChange(this.props.highlights);
                                            }}
                                        />
                                    </TableRowColumn>
                                    <TableRowColumn
                                        key='highlight-tr-2nd-col'
                                        style={tableCellCommonStyle}
                                    >
                                        <TextField
                                            key={'highlight-to' + index}
                                            id={'highlight-to' + index}
                                            type='number'
                                            value={highlight.to}
                                            min={this.props.minValue}
                                            max={this.props.maxValue}
                                            fullWidth={true}
                                            errorText={(highlight.to < this.props.minValue ||
                                                highlight.to > this.props.maxValue ||
                                                highlight.to < highlight.from ||
                                                _.isNaN(highlight.to)) ? ' ' : ''}
                                            onChange={(event: object, newValue: string) => {
                                                this.props.highlights[index].to = this._parseValue(newValue);
                                                this._onChange(this.props.highlights);
                                            }}
                                        />
                                    </TableRowColumn>
                                    <TableRowColumn
                                        key='highlight-tr-3rd-col'
                                        style={tableCellCommonStyle}
                                    >
                                        {this._renderColorPicker(highlight, index, false)}
                                    </TableRowColumn>
                                    {this.props.showTextColor
                                        ? <TableRowColumn
                                            key='highlight-tr-4rd-col'
                                            style={tableCellCommonStyle}>
                                            {this._renderColorPicker(highlight, index, true)}
                                        </TableRowColumn>
                                        : null}
                                    <TableRowColumn
                                        key='highlight-tr-5th-col'
                                        style={tableCellCommonStyle}
                                    >
                                        <IconButton
                                            key={'delete-highlight-' + index}
                                            onClick={() => {
                                                this.setState((s) => {
                                                    s.showColorPicker[index] = false;
                                                    s.showTextColorPicker[index] = false;
                                                    return s;
                                                }, () => {
                                                    this.props.highlights.splice(index, 1)
                                                    this._onChange(this.props.highlights);
                                                });
                                            }}
                                            disableFocusRipple={!isAnimationEnabled()}
                                            disableTouchRipple={!isAnimationEnabled()}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableRowColumn>
                                </TableRow>
                            );
                        })}
                        updateItemsOrder={(oldIndex: number, newIndex: number) => {
                            this._onChange(arrayMoveImmutable(this.props.highlights, oldIndex, newIndex));
                        }}
                    />
                </React.Suspense>
            </div>
        );
    }
    // ------------------------------------------------------------------------------------------------------
    private _parseValue(newValue: string): number {
        if (this.props.enableDecimal)
            return parseFloat(newValue);
        return parseInt(newValue, 10);
    }
}
// ------------------------------------------------------------------------------------------------------
export interface MultiHighlightsEditorProps {

    /**
     * Whether dialog is visible or not
     */
    isVisible: boolean;

    /*
     * width/height to render
     */
    width: number;
    height: number;

    /**
     * Highlights of each layer
     */
    layer2Highlights: { [key: string]: GaugeHelperHighlight[] };

    /**
     * Whether to diplay color picker for text color picking, or not.
     */
    showTextColor?: boolean;

    /**
     * Minimum integer value
     */
    minValue: number;

    /**
     *
     * Maximum integer value
     */
    maxValue: number;

    /**
     *
     * Whether to enable decimal values in highlights
     */
    enableDecimal: boolean;

    /**
     * Callback when user requests save
     */
    onSave: (newHighlights: { [key: string]: GaugeHelperHighlight[] }) => void;

    /**
     * Callback when user aborts
     */
    onCancel: () => void;
}
// ------------------------------------------------------
export interface MultiHighlightsEditorState {
    /*
     * layers information
     */

    layer2Highlights: { [key: string]: GaugeHelperHighlight[] };
    selectedLayerId: string;

    /*
     *
     */
    showList: boolean;
    showErrorDialog: boolean;
    errorMessage: React.ReactElement<any>;

    /*
     * Fields for layer addition/update
     */
    textFieldValue: string;
    textFieldError: string;
    layerDialogOpen: boolean;
    layerIdToEdit: string;

    /*
     * Fields for search bar handling
     */
    showSearchBar: boolean;
    searchKeys: string[];
    emptyValue: boolean;

    SubDialogConf: DialogConf;
    SubDialogConfstyle: React.CSSProperties;
}

export class MultiHighlightsEditor extends React.Component<MultiHighlightsEditorProps, MultiHighlightsEditorState>{
    private _textFieldRef: TextField = null;
    private _searchBarRef: SearchBar = null;
    // ---------------------------------------------------------------------------------------------------
    constructor(props: MultiHighlightsEditorProps) {
        super(props);

        this.state = {
            layer2Highlights: isDefined(this.props.layer2Highlights) ? _.cloneDeep(this.props.layer2Highlights) : {},
            selectedLayerId: '',
            showErrorDialog: false,
            errorMessage: null,
            textFieldValue: '',
            textFieldError: '',
            layerDialogOpen: false,
            layerIdToEdit: null,
            showList: true,
            showSearchBar: false,
            emptyValue: true,
            searchKeys: [],
            SubDialogConf: null,
            SubDialogConfstyle: {}
        };

        this._onSave = this._onSave.bind(this);
        this._onCancel = this._onCancel.bind(this);
        this._addOrUpdateLayer = this._addOrUpdateLayer.bind(this);
        this._focusTextfield = this._focusTextfield.bind(this);
        this._focusSearchbar = this._focusSearchbar.bind(this);
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: MultiHighlightsEditorProps) {
        // catch highlights from parent
        if (_.isEqual(this.props.layer2Highlights, prevProps.layer2Highlights)) {
            this.setState({ layer2Highlights: this.props.layer2Highlights });
        }
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        let theme = BoardTheming.GetActiveTheme();
        const listWidth = !this.state.showList ? 0 : (this.props.width > 300 ? 300 : 80);
        const toolbarHeight = this.props.height > theme.genericToolbars.height ? theme.genericToolbars.height : theme.genericToolbars.height / 2;
        return (
            <div>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        height: this.props.height,
                        width: this.props.width,
                        overflow: 'hidden'
                    }}
                >
                    <Toolbar key="title-toolbar" style={{
                        height: toolbarHeight, width: this.props.width,
                        boxShadow: "rgba(0, 0, 0, 0.16) 0px 3px 10px, rgba(0, 0, 0, 0.23) 0px 3px 10px",
                        position: "relative", color: "white", backgroundColor: "rgb(19, 68, 89)", zIndex: 1000
                    }} >
                        <ToolbarGroup>
                            <ToolbarTitle style={{ color: "white" }} key="title-toolbar-title" text={"Highlights Editor"} />
                        </ToolbarGroup>
                        <ToolbarGroup style={{ color: "white" }} lastChild={true}>
                            <div style={{
                                boxSizing: 'border-box',
                                textAlign: 'right',
                                display: "table",
                                marginRight: 10
                            }}>
                                <FlatButton
                                    label='Cancel'
                                    secondary={true}
                                    onClick={this._onCancel}
                                />
                                <FlatButton
                                    label='Save'
                                    primary={true}
                                    onClick={this._onSave}
                                />
                            </div>
                        </ToolbarGroup>
                    </Toolbar>

                    <div style={{ width: this.props.width, height: this.props.height - toolbarHeight }}>
                        {/* List */}
                        {listWidth > 0 ? this._renderList(listWidth, this.props.height - toolbarHeight, toolbarHeight) : null}

                        {/* Main view */}
                        {this._renderMainView(this.props.width - listWidth, this.props.height - toolbarHeight, toolbarHeight)}
                    </div>
                </div>

                {/* Dialog for saving errors */}
                {
                    (this.state.SubDialogConf == null) ?
                        null :
                        <ExDialog
                            key={"-dialog-sub"}
                            Enable={this.state.SubDialogConf != null}
                            Conf={this.state.SubDialogConf}
                            style={this.state.SubDialogConfstyle}
                        />
                }

                {/* Dialog for layer addition/update */}
                <Dialog
                    key='layer-id-dialog'
                    modal={true}
                    open={this.state.layerDialogOpen}
                    title={isDefined(this.state.layerIdToEdit) ? 'Edit layer' : 'Add layer'}
                    bodyStyle={{ padding: 30 }}
                    autoScrollBodyContent={true}
                    actions={
                        [
                            <FlatButton
                                label='Cancel'
                                primary={true}
                                onClick={() => {
                                    // just close dialog
                                    this.setState({ layerDialogOpen: false });
                                }}
                            />,
                            <FlatButton
                                label={isDefined(this.state.layerIdToEdit) ? 'Save' : 'Add'}
                                primary={true}
                                onClick={() => {
                                    this._addOrUpdateLayer();
                                }}
                            />
                        ]
                    }
                >
                    <TextField
                        ref={(r) => { this._textFieldRef = r; }}
                        floatingLabelFixed={true}
                        errorText={this.state.textFieldError}
                        floatingLabelText='Name'
                        key='layer-id-textfield'
                        id='layer-id-textfield'
                        value={this.state.textFieldValue}
                        onKeyDown={(event: React.KeyboardEvent<any>) => {
                            // Enter was pressed ?
                            if ((isDefined(event.which) ? event.which : event.keyCode) == 13) {
                                // question: preventDefault needed ?
                                event.preventDefault();
                                this._addOrUpdateLayer();
                            }
                        }}
                        onChange={(event: object, newValue: string) => {
                            this.setState({ textFieldValue: newValue, textFieldError: '' });
                        }}
                    />

                </Dialog>
            </div >
        );
    }
    // ---------------------------------------------------------------------------------------------------
    private _onSave() {
        let erroredLayers: string[] = [];
        // validate all layers; keep ids with errors
        _.forEach(this.state.layer2Highlights, (layer, key) => {
            if (!validateHighlights(this.props.minValue, this.props.maxValue, layer)) {
                erroredLayers.push(key);
            }
        });
        if (erroredLayers.length == 0) {
            if (isDefined(this.props.onSave))
                try {
                    this.props.onSave(this.state.layer2Highlights);
                }
                catch (ex) {
                    DebugEx.TraceError(ex);
                }
        }
        else {
            this.setState({
                SubDialogConf: {
                    title: "Error",
                    actions: [
                        <FlatButton
                            label='Ok'
                            primary={true}
                            onClick={() => this.setState({ SubDialogConf: null })}
                        />
                    ],
                    body: <div style={{ padding: 24 }}>
                        <div>
                            <p>Please check again your <b>'from'</b> and <b>'to'</b> fields which should be included betweeen min and max values.</p>
                            <p>Layers containing errors:</p>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    flexWrap: 'wrap'
                                }}
                            >
                                {_.map(erroredLayers, (layerId) => {
                                    return <Chip
                                        style={{ margin: 4 }}
                                    >
                                        {layerId}
                                    </Chip>
                                })}
                            </div>
                        </div>
                    </div>
                },
                SubDialogConfstyle: { marginTop: 100 }
            });
        }
    }
    // ---------------------------------------------------------------------------------------------------
    private _onCancel() {
        if (isDefined(this.props.onCancel))
            try {
                this.props.onCancel();
            }
            catch (ex) {
                DebugEx.TraceError(ex);
            }
    }
    // ---------------------------------------------------------------------------------------------------
    // todo: apply checks
    private _addOrUpdateLayer() {

        // check for duplicate
        if (isDefined(this.state.layer2Highlights[this.state.textFieldValue])) {
            this.setState({ textFieldError: 'Duplicate layer id' });
            return;
        }

        //ok, procced with add/update
        this.setState((s: MultiHighlightsEditorState) => {
            // check action
            if (isDefined(s.layerIdToEdit)) {
                // get highlights
                const highlights = s.layer2Highlights[s.selectedLayerId];

                // add new entry
                s.layer2Highlights[s.textFieldValue] = highlights;

                // delete old entry
                delete s.layer2Highlights[s.selectedLayerId];
            }
            else {
                s.layer2Highlights[s.textFieldValue] = [{
                    from: this.props.minValue,
                    to: this.props.maxValue,
                    color: 'rgba(0,0,0,1)',
                    textColor: 'rgba(0,0,0,1)'
                }];
            }

            // select this layer
            s.selectedLayerId = s.textFieldValue;

            // empty textField variables
            s.textFieldValue = '';
            s.textFieldError = '';

            // close dialog
            s.layerDialogOpen = false;
            return s;
        });

    }
    // ---------------------------------------------------------------------------------------------------
    private _focusTextfield() {
        setTimeout(() => {
            if (isDefined(this._textFieldRef))
                this._textFieldRef.focus();
        }, 150);
    }
    // ---------------------------------------------------------------------------------------------------
    private _focusSearchbar() {
        setTimeout(() => {
            if (isDefined(this._searchBarRef))
                this._searchBarRef.focus();
        }, 50);
    }
    // ---------------------------------------------------------------------------------------------------
    private _renderList(width: number, height: number, toolbarHeight: number): React.ReactElement<any> {
        const searchbarHeight = !this.state.showSearchBar ? 0 : ((height - toolbarHeight) > 48 ? 48 : 20);
        const layerIDs = this.state.emptyValue ? _.keys(this.state.layer2Highlights) : this.state.searchKeys;
        const theme = BoardTheming.GetActiveTheme();
        return (
            <div
                key='list-view'
                style={{
                    height: height,
                    width: width,
                    overflow: 'hidden',
                    borderRight: '1px solid ' + grey300,
                    float: "left"
                }}
            >
                <Toolbar
                    key='layers-list-toolbar'
                    style={{
                        position: "relative",
                        boxShadow: getZDepthShadow(theme.genericToolbars.style.shadowLevel),
                        color: theme.genericToolbars.style.fontColor,
                        height: toolbarHeight,
                        backgroundColor: theme.genericToolbars.style.backgroundColor
                    }}
                >
                    <ToolbarGroup>

                    </ToolbarGroup>
                    <ToolbarGroup>
                        <IconButton
                            key='add-layer-icon'
                            touch={true}
                            tooltip='Add new layer'
                            tooltipPosition='bottom-left'
                            onClick={() => {
                                this.setState({
                                    layerDialogOpen: true,
                                    layerIdToEdit: null
                                }, this._focusTextfield)
                            }}
                            disableFocusRipple={!isAnimationEnabled()}
                            disableTouchRipple={!isAnimationEnabled()} >
                            <AddIcon color="white" />
                        </IconButton>
                        <IconButton
                            key='toggle-searchbar-icon'
                            touch={true}
                            onClick={() => {
                                this.setState({
                                    showSearchBar: !this.state.showSearchBar
                                }, this._focusSearchbar)
                            }}
                            disableFocusRipple={!isAnimationEnabled()}
                            disableTouchRipple={!isAnimationEnabled()}
                        >
                            <SearchIcon color="white" />
                        </IconButton>
                    </ToolbarGroup>
                </Toolbar >

                <div style={{
                    width: width,
                    height: searchbarHeight,
                    overflow: 'hidden',
                    padding: 0
                }}>
                    <SearchBar
                        ref={(r) => { this._searchBarRef = r; }}
                        simple={true}
                        key={'search-bar'}
                        style={{
                            maxWidth: width,
                            backgroundColor: theme.genericToolbars.style.subBackgroundColor,
                            borderRadius: undefined
                        }}
                        source={_.map(this.state.layer2Highlights, (layer, key) => {
                            return { key: key, value: key };
                        })}
                        onSearch={(keys, emptySearchValue) => {
                            this.setState({
                                searchKeys: isDefined(keys) ? keys : [],
                                emptyValue: emptySearchValue
                            });
                        }}
                    />
                </div>
                {layerIDs.length == 0 ?
                    <div>
                        <p
                            style={{ textAlign: 'center', padding: 5 }}
                        >
                            {this.state.emptyValue ? 'No layers' : 'No matching layers found'}
                        </p>
                    </div> :

                    <List
                        style={{
                            height: height - toolbarHeight - searchbarHeight,
                            width: width,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            padding: 0
                        }}
                    >
                        {_.map(layerIDs, (layerId) => {
                            return <ListItem
                                key={layerId}
                                primaryText={layerId}
                                style={this.state.selectedLayerId === layerId ? { backgroundColor: "rgba(0, 0, 0, 0.2)" } : {}}
                                onClick={() => {
                                    this.setState({ selectedLayerId: layerId })
                                }}
                                rightIconButton={
                                    <IconMenu
                                        style={{
                                            marginLeft: 0,
                                            marginBottom: 0,
                                            marginTop: 0,
                                            marginRight: 12
                                        }}
                                        iconButtonElement={
                                            <IconButton
                                                disableFocusRipple={!isAnimationEnabled()}
                                                disableTouchRipple={!isAnimationEnabled()}
                                            >
                                                <MoreVertIcon color={grey500} />
                                            </IconButton>
                                        }
                                    >
                                        <MenuItem
                                            disableFocusRipple={!isAnimationEnabled()}
                                            disableTouchRipple={!isAnimationEnabled()}
                                            leftIcon={<EditIcon />}
                                            onClick={() => {
                                                this.setState({
                                                    layerDialogOpen: true,
                                                    layerIdToEdit: layerId,
                                                    textFieldValue: layerId,
                                                    textFieldError: ''
                                                }, this._focusTextfield);
                                            }}
                                        >
                                            Edit layer
                                        </MenuItem>
                                        <MenuItem
                                            disableFocusRipple={!isAnimationEnabled()}
                                            disableTouchRipple={!isAnimationEnabled()}
                                            leftIcon={<DeleteIcon />}
                                            onClick={() => {
                                                // question: prompt for user confirmation?
                                                this.setState((s: MultiHighlightsEditorState) => {
                                                    // if deleting selected layer; deselect
                                                    if (s.selectedLayerId === layerId)
                                                        s.selectedLayerId = '';
                                                    delete s.layer2Highlights[layerId];
                                                    return s;
                                                })
                                            }}
                                        >
                                            Delete layer
                                        </MenuItem>
                                    </IconMenu>
                                }
                            />
                        })}
                    </List>
                }
            </div>
        )
    }
    // ---------------------------------------------------------------------------------------------------
    private _renderMainView(width: number, height: number, toolbarHeight: number): React.ReactElement<any> {
        const theme = BoardTheming.GetActiveTheme();
        return (
            <div
                key='main-view'
                style={{
                    height: height,
                    width: width,
                    overflow: 'hidden',
                    float: "right"
                }}
            >
                <Toolbar
                    key='highlights-editor-toolbar'
                    style={{
                        position: "relative",
                        boxShadow: getZDepthShadow(theme.genericToolbars.style.shadowLevel),
                        color: theme.genericToolbars.style.fontColor,
                        height: toolbarHeight,
                        backgroundColor: theme.genericToolbars.style.backgroundColor
                    }}
                >
                    <ToolbarGroup firstChild={true}>
                        {this.state.showList ?
                            <IconButton
                                key='hide-list-icon'
                                touch={true}
                                onClick={() => {
                                    this.setState({ showList: false })
                                }}
                                disableFocusRipple={!isAnimationEnabled()}
                                disableTouchRipple={!isAnimationEnabled()} >
                                <KeyboardLeftArrowIcon color='white' />
                            </IconButton>
                            :
                            <IconButton
                                key='show-list-icon'
                                touch={true}
                                onClick={() => {
                                    this.setState({ showList: true })
                                }}
                                disableFocusRipple={!isAnimationEnabled()}
                                disableTouchRipple={!isAnimationEnabled()}
                            >
                                <KeyboardRightArrowIcon color='white' />
                            </IconButton>
                        }
                        <ToolbarTitle
                            style={{ color: 'white' }}
                            text={IsNullOrEmpty(this.state.selectedLayerId) ?
                                'Highlights' :
                                ('Highlights: ' + this.state.selectedLayerId)
                            }

                        />
                    </ToolbarGroup>
                    {
                        !IsNullOrEmpty(this.state.selectedLayerId) ?
                            <ToolbarGroup>
                                <IconButton
                                    key='add-new-highlight-icon-button'
                                    tooltip='Add new Highlight'
                                    tooltipPosition='bottom-left'
                                    touch={true}
                                    onClick={() => {
                                        this.setState((s) => {
                                            s.layer2Highlights[s.selectedLayerId].push({
                                                from: this.props.minValue,
                                                to: this.props.maxValue,
                                                color: 'rgba(0,0,0,1)',
                                                textColor: 'rgba(0,0,0,1)'
                                            });
                                            return s;
                                        });
                                    }}
                                    disableFocusRipple={!isAnimationEnabled()}
                                    disableTouchRipple={!isAnimationEnabled()}
                                >
                                    <AddIcon color='white' />
                                </IconButton>
                            </ToolbarGroup>
                            : null
                    }
                </Toolbar>
                {
                    IsNullOrEmpty(this.state.selectedLayerId) ?
                        <Paper
                            key={"paper-empty"}
                            className="noselect"
                            zDepth={2}
                            rounded={true}
                            transitionEnabled={false}
                            style={{
                                width: (width) / 2,
                                height: (height - toolbarHeight) / 2,
                                textAlign: 'center',
                                margin: '20px auto',
                                fontSize: 'x-large',
                                fontWeight: 700
                            }}>
                            <p>
                                <IconButton
                                    iconStyle={{ width: 60, height: 60 }}
                                    style={{ width: 120, height: 120 }}
                                    disableFocusRipple={!isAnimationEnabled()}
                                    disableTouchRipple={!isAnimationEnabled()}
                                >
                                    <OpenIcon />
                                </IconButton>
                            </p>
                            <p>Please select a layer</p>
                        </Paper >

                        :
                        <SortableHighlightsTable
                            key={this.state.selectedLayerId}
                            height={height - toolbarHeight}
                            width={width}
                            highlights={this.state.layer2Highlights[this.state.selectedLayerId]}
                            onChange={(newHighlights) => {
                                this.setState((s) => {
                                    s.layer2Highlights[s.selectedLayerId] = newHighlights;
                                    return s;
                                });
                            }}
                            minValue={this.props.minValue}
                            maxValue={this.props.maxValue}
                            showTextColor={this.props.showTextColor}
                            enableDecimal={this.props.enableDecimal}
                        />
                }
            </div>
        );
    }
    // ---------------------------------------------------------------------------------------------------
}